import React, { Component } from 'react';
import _AdminService from 'modules/AdminService';
import _notification from 'modules/notification';
import { Card, Modal } from 'antd';
import PageHeader from 'components/enterprise/PageHeader';
import LoadingSpinner from 'components/enterprise/LoadingSpinner';
import EmptyState from 'components/enterprise/EmptyState';
import DropdownList from 'components/controls/DropdownList';
import ButtonComponent from 'components/controls/Button';
import ConfirmPopup from '../popup/ConfirmPopup';
import { routesAdmin } from 'routes/Routes';
import { GENERAL_ERROR_MESSAGE } from 'constants/constants';

// Primary Result Automation & Interview Shortlisting phase.
//
// Replaces the manual "type the literal string NULL into shortlisted candidates' Marks cells before
// upload" workaround: the admin now imports the real Primary Result (unchanged, via the existing
// Upload Result screen), then comes here to compute the Interview Shortlist explicitly — never
// automatically as a side effect of import (frozen requirement, implementation prompt §6).
//
// Frozen rules this screen surfaces but never invents:
//   - Interview Shortlist = Post.Vacancy x Interview Ratio (default 3), server-computed.
//   - Tie handling: every candidate tied with the cutoff score is included — this screen only
//     displays what the server already decided; it applies no tiebreaker of its own.
//   - Recomputing an existing shortlist requires an explicit second confirmation (never silent).
class InterviewShortlist extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            posts: [],
            selectedPostId: null,
            preview: null,
            previewLoading: false,
            showConfirm: false,
            confirming: false,
            exporting: false
        };
    }

    componentDidMount() {
        this.loadPosts();
    }

    loadPosts = async () => {
        this.setState({ loading: true });
        try {
            const response = await _AdminService.GetAllPost();
            this.setState({ posts: response.data || [], loading: false });
        } catch (e) {
            this.setState({ loading: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    getSelectedPost = () => (this.state.posts || []).find(p => p.Id === this.state.selectedPostId) || null;

    onPostChange = (e) => {
        const postId = parseInt(e.target.value, 10) || null;
        this.setState({ selectedPostId: postId, preview: null }, () => {
            if (postId) {
                this.loadPreview();
            }
        });
    }

    loadPreview = async () => {
        this.setState({ previewLoading: true });
        try {
            const response = await _AdminService.PreviewInterviewShortlist(this.state.selectedPostId);
            if (response.status === 200 && response.data.IsSuccess) {
                this.setState({ preview: response.data.Obj, previewLoading: false });
            } else {
                this.setState({ previewLoading: false });
                _notification.showMessage('error', response.data.Message || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ previewLoading: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    openConfirm = () => {
        if (!this.state.preview) {
            _notification.showMessage('error', 'Select a Post first.');
            return;
        }
        if (!this.state.preview.Vacancy) {
            _notification.showMessage('error', 'This Post has no Vacancy configured yet — set it on the Post Master screen first.');
            return;
        }
        this.setState({ showConfirm: true });
    }

    handleConfirm = async () => {
        this.setState({ showConfirm: false, confirming: true });
        try {
            const response = await _AdminService.ConfirmInterviewShortlist(this.state.selectedPostId, this.state.preview.AlreadyShortlistedExists);
            if (response.status === 200 && response.data.IsSuccess) {
                this.setState({ confirming: false, preview: response.data.Obj });
                _notification.showMessage('success', `Interview Shortlist generated for ${response.data.Obj.PostName} (${response.data.Obj.ShortlistedCount} candidate(s)).`);
            } else {
                this.setState({ confirming: false });
                _notification.showMessage('error', response.data.Message || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ confirming: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    handleExport = async () => {
        this.setState({ exporting: true });
        try {
            const response = await _AdminService.ExportInterviewBlindData(this.state.selectedPostId);
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const post = this.getSelectedPost();
            const a = document.createElement('a');
            a.href = url;
            a.download = `InterviewData_${post ? post.Name.replace(/\s+/g, '_') : this.state.selectedPostId}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            this.setState({ exporting: false });
        } catch (e) {
            this.setState({ exporting: false });
            _notification.showMessage('error', 'Could not generate the interview-blind export. Confirm a shortlist exists for this Post.');
        }
    }

    render() {
        const { loading, posts, preview, previewLoading, confirming, exporting } = this.state;

        if (loading) return <LoadingSpinner />

        return (
            <div className="container-fluid p-3">
                <PageHeader
                    title="Interview Shortlist"
                    breadcrumbItems={[{ label: 'Dashboard', path: routesAdmin.dashboard.path }, { label: 'Candidates' }, { label: 'Interview Shortlist' }]}
                />
                <div className="max1200 m-auto">
                    <Card>
                        {posts.length === 0 ? (
                            <EmptyState icon="team" title="No Posts Found" description="Create a Post/Exam before computing an Interview Shortlist." />
                        ) : (
                            <>
                                <div className="row">
                                    <div className="col-sm-6 mb-3">
                                        <DropdownList
                                            value={this.state.selectedPostId || 0}
                                            name="postId"
                                            onChange={this.onPostChange}
                                            placeholder="Select Post"
                                            heading="Post/Exam"
                                            className="w-100"
                                            wrapClass="wrap"
                                            isRequired={true}
                                            options={posts}
                                        />
                                    </div>
                                </div>

                                {previewLoading && <LoadingSpinner />}

                                {!previewLoading && preview && (
                                    <div className="mb-3">
                                        <p><b>Post:</b> {preview.PostName}</p>
                                        <p><b>Vacancy:</b> {preview.Vacancy != null ? preview.Vacancy : <span className="color-red">Not configured — set it on Post Master first</span>}</p>
                                        <p><b>Interview Ratio:</b> {preview.InterviewRatio}</p>
                                        <p><b>Nominal Shortlist Count:</b> {preview.NominalShortlistCount}</p>
                                        <p><b>Cutoff Marks:</b> {preview.CutoffMarks != null ? preview.CutoffMarks : 'N/A'}</p>
                                        <p><b>Candidates Considered:</b> {preview.CandidatesConsidered} ({preview.CandidatesExcludedNonRankable} excluded — no valid Marks)</p>
                                        <p><b>Would Be Shortlisted:</b> {preview.ShortlistedCount}{preview.TieCount > 0 ? ` (including ${preview.TieCount} tied at the cutoff score)` : ''}</p>
                                        {preview.AlreadyShortlistedExists && (
                                            <p className="color-red"><b>An Interview Shortlist already exists for this Post ({preview.AlreadyShortlistedCount} candidate(s)). Generating again will replace it.</b></p>
                                        )}

                                        <div className="pt-2">
                                            <ButtonComponent
                                                type="button"
                                                value={confirming ? 'Generating…' : 'Generate Interview Shortlist'}
                                                onClick={this.openConfirm}
                                                className="button-global button-primary autoHeight small mr-2"
                                                disabled={confirming}
                                            />
                                            <ButtonComponent
                                                type="button"
                                                value={exporting ? 'Preparing…' : 'Download Interview Data'}
                                                onClick={this.handleExport}
                                                className="button-global button-border autoHeight small"
                                                disabled={exporting || preview.AlreadyShortlistedCount === 0 && preview.ShortlistedCount === 0}
                                            />
                                        </div>

                                        {preview.ShortlistedCandidates && preview.ShortlistedCandidates.length > 0 && (
                                            <div className="pt-3">
                                                <table className="table">
                                                    <thead>
                                                        <tr><th>Roll Number</th><th>Name</th><th>Marks</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {preview.ShortlistedCandidates.map((c, i) => (
                                                            <tr key={i}><td>{c.RollNumber}</td><td>{c.StudentName}</td><td>{c.Marks}</td></tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </Card>
                </div>

                <Modal
                    centered={true}
                    width={480}
                    className="modelPopAnt admin-home"
                    visible={this.state.showConfirm}
                    onCancel={() => this.setState({ showConfirm: false })}
                    footer={false}
                >
                    {preview && (
                        <ConfirmPopup
                            onCancel={() => this.setState({ showConfirm: false })}
                            onOk={this.handleConfirm}
                            message={`Generate the Interview Shortlist for ${preview.PostName}? Vacancy: ${preview.Vacancy}, Ratio: ${preview.InterviewRatio}, Nominal: ${preview.NominalShortlistCount}, Cutoff Marks: ${preview.CutoffMarks}, Candidates Shortlisted: ${preview.ShortlistedCount}${preview.TieCount > 0 ? ` (${preview.TieCount} tied at the cutoff)` : ''}.${preview.AlreadyShortlistedExists ? ' This will REPLACE the existing shortlist of ' + preview.AlreadyShortlistedCount + ' candidate(s).' : ''}`}
                        />
                    )}
                </Modal>
            </div>
        )
    }
}

export default InterviewShortlist;
